/**
 * Artists Service
 * Fetches artist data from Firebase, including legacy Breakin' Bread artists
 */

import { db } from '@/lib/firebase';
import { normalizeGenres } from '@/lib/genres';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import type { Artist } from '@/types/user';

export interface ArtistProfile {
  id: string;
  name: string;
  slug: string;
  bio: string;
  longBio?: string;
  genres: string[];
  imageUrl?: string;
  coverImageUrl?: string;
  profileLink?: string;
  labelId?: string;
  labelName?: string;
  signedYear?: number;
  members?: {
    name: string;
    role?: string;
    alsoKnownAs?: string[];
  }[];
  origin?: {
    city?: string;
    country: string;
  };
  yearsActive?: {
    from: number;
    to?: number | null;
  };
  socialLinks?: Record<string, string>;
  popularity?: number;
  type?: string;
  dateAdded?: string;
  dataAiHint?: string;
  // Gallery & media
  images?: string[];       // gallery items (URLs) — images, GIFs, and mp4/webm clips mixed together; see isVideoUrl()
  videoUrl?: string;       // primary video (YouTube URL or embed)
  videos?: string[];       // additional videos (YouTube URLs or direct links)
  /** Per-artist override for the card thumbnail's autoplay slideshow speed —
   *  falls back to the site-wide Super Admin default (slideshowIntervalSec/
   *  slideshowFadeSec/slideshowTransition) when unset (Gordon 2026-09-20:
   *  "default tempo and fades that users can custom"). */
  slideshowSettings?: { intervalSec?: number; fadeSec?: number; transition?: string };
  discogs?: string;        // Discogs artist ID
  // Visibility fields
  hidden?: boolean;        // user can hide their own profile
  adminHidden?: boolean;   // super admin override — hides regardless of user setting
  status?: string;         // 'active' | 'suspended' | 'pending'
  // Connected accounts — user UIDs of creators/contributors linked to this artist
  // (band members, producers, label contacts, etc.)
  connectedUserIds?: string[];
  /** Primary owner — the user UID that controls / claims this artist.
   *  Always also present in connectedUserIds; this field just marks the lead.
   *  Set by linkUserToArtist() in services/artist-link.ts. */
  ownerUid?: string;
}

const ARTISTS_COLLECTION = 'artists';

/**
 * Get all artists with optional filtering
 */
export async function getArtists(options?: {
  labelId?: string;
  genre?: string;
  limitCount?: number;
  lastDoc?: DocumentSnapshot;
}): Promise<{ artists: ArtistProfile[]; lastDoc: DocumentSnapshot | null }> {
  try {
    let q = query(collection(db, ARTISTS_COLLECTION));

    // Filter by label if specified
    if (options?.labelId) {
      q = query(q, where('labelId', '==', options.labelId));
    }

    // Filter by genre if specified
    if (options?.genre) {
      q = query(q, where('genres', 'array-contains', options.genre));
    }

    // Order by popularity
    q = query(q, orderBy('popularity', 'desc'));

    // Limit results
    if (options?.limitCount) {
      q = query(q, limit(options.limitCount));
    }

    // Pagination
    if (options?.lastDoc) {
      q = query(q, startAfter(options.lastDoc));
    }

    const snapshot = await getDocs(q);
    const artists: ArtistProfile[] = [];
    let lastDocument: DocumentSnapshot | null = null;

    snapshot.forEach((doc) => {
      const raw = doc.data();
      // Normalise genres: prefer genres[] but fall back to legacy genre string
      const genres = normalizeGenres(Array.isArray(raw.genres) && raw.genres.length > 0 ? raw.genres : (raw.genre ? [raw.genre as string] : []));
      const data = { id: doc.id, ...raw, genres } as ArtistProfile;
      // Skip artists hidden by super admin OR without a profile image
      // (no thumbnail = not ready for public display)
      if (!data.adminHidden && data.imageUrl) {
        artists.push(data);
        lastDocument = doc;
      }
    });

    return { artists, lastDoc: lastDocument };
  } catch (error) {
    console.error('Error fetching artists:', error);
    return { artists: [], lastDoc: null };
  }
}

/**
 * Get a single artist by ID
 */
export async function getArtistById(id: string): Promise<ArtistProfile | null> {
  try {
    const docRef = doc(db, ARTISTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const raw = docSnap.data();
      const genres = normalizeGenres(Array.isArray(raw.genres) && raw.genres.length > 0 ? raw.genres : (raw.genre ? [raw.genre as string] : []));
      return { id: docSnap.id, ...raw, genres } as ArtistProfile;
    }
    return null;
  } catch (error) {
    console.error('Error fetching artist:', error);
    return null;
  }
}

/**
 * Get every artist doc a user is connected to — via connectedUserIds
 * (array-contains), ownerUid, or the legacy linkedUserId field. Same
 * 3-query pattern already inlined in profile/page.tsx's media loader;
 * pulled out here so anything needing "does this user have an artist
 * profile" (e.g. custom-domain connection) doesn't duplicate it again.
 */
export async function getArtistsForUser(uid: string): Promise<ArtistProfile[]> {
  if (!uid) return [];
  try {
    const snaps = await Promise.allSettled([
      getDocs(query(collection(db, ARTISTS_COLLECTION), where('connectedUserIds', 'array-contains', uid))),
      getDocs(query(collection(db, ARTISTS_COLLECTION), where('ownerUid', '==', uid))),
      getDocs(query(collection(db, ARTISTS_COLLECTION), where('linkedUserId', '==', uid))),
    ]);
    const byId = new Map<string, ArtistProfile>();
    snaps.forEach(s => {
      if (s.status !== 'fulfilled') return;
      s.value.docs.forEach(d => {
        if (byId.has(d.id)) return;
        const raw = d.data();
        const genres = normalizeGenres(Array.isArray(raw.genres) && raw.genres.length > 0 ? raw.genres : (raw.genre ? [raw.genre as string] : []));
        byId.set(d.id, { id: d.id, ...raw, genres } as ArtistProfile);
      });
    });
    return Array.from(byId.values());
  } catch (error) {
    console.error('Error fetching artists for user:', error);
    return [];
  }
}

/**
 * Get a single artist by slug
 */
/** Canonical slug derivation used as the fallback when an artist doc doesn't
 *  carry an explicit `slug` field (older imports / migrations). Matches the
 *  same rules the music page uses when minting the fallback link. */
function slugify(s: string): string {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // strip diacritics
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export async function getArtistBySlug(slug: string): Promise<ArtistProfile | null> {
  try {
    // 1) Exact slug match — fastest, indexed.
    const q = query(
      collection(db, ARTISTS_COLLECTION),
      where('slug', '==', slug),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const snap = snapshot.docs[0];
      const raw = snap.data();
      const genres = normalizeGenres(Array.isArray(raw.genres) && raw.genres.length > 0 ? raw.genres : (raw.genre ? [raw.genre as string] : []));
      return { id: snap.id, ...raw, genres } as ArtistProfile;
    }

    // 2) Name-match fallback — handles releases imported without a stored slug.
    //    The music page links to `/artists/${slugify(artistName)}` when the
    //    release has no artistId, so we need to be able to resolve that slug
    //    back to a real artist doc.
    const humanized = slug.replace(/-/g, " ").trim();
    if (humanized) {
      const nameQ = query(
        collection(db, ARTISTS_COLLECTION),
        where('name', '==', humanized),
        limit(1)
      );
      const nameSnap = await getDocs(nameQ);
      if (!nameSnap.empty) {
        const snap = nameSnap.docs[0];
        const raw = snap.data();
        const genres = normalizeGenres(Array.isArray(raw.genres) && raw.genres.length > 0 ? raw.genres : (raw.genre ? [raw.genre as string] : []));
        return { id: snap.id, ...raw, genres } as ArtistProfile;
      }
    }

    // 3) Last resort — small linear scan that matches on slugify(name).
    //    Catches casing / punctuation mismatches like "Don Skills" → "don-skills".
    //    Capped at 1000 reads; BB instances are well below that.
    const allQ = query(collection(db, ARTISTS_COLLECTION), limit(1000));
    const allSnap = await getDocs(allQ);
    for (const d of allSnap.docs) {
      const raw = d.data() as any;
      if (raw.slug === slug) continue; // already missed in step 1
      const candidateSlug = slugify(raw.name || "");
      if (candidateSlug && candidateSlug === slug) {
        const genres = normalizeGenres(Array.isArray(raw.genres) && raw.genres.length > 0 ? raw.genres : (raw.genre ? [raw.genre as string] : []));
        return { id: d.id, ...raw, genres } as ArtistProfile;
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching artist by slug:', error);
    return null;
  }
}

/**
 * Get featured/popular artists for homepage
 */
export async function getFeaturedArtists(count: number = 6): Promise<ArtistProfile[]> {
  try {
    const q = query(
      collection(db, ARTISTS_COLLECTION),
      orderBy('popularity', 'desc'),
      limit(count)
    );
    const snapshot = await getDocs(q);

    const artists: ArtistProfile[] = [];
    snapshot.forEach((doc) => {
      const raw = doc.data();
      const genres = normalizeGenres(Array.isArray(raw.genres) && raw.genres.length > 0 ? raw.genres : (raw.genre ? [raw.genre as string] : []));
      const data = { id: doc.id, ...raw, genres } as ArtistProfile;
      // Skip artists hidden by super admin OR without a profile image
      if (!data.adminHidden && data.imageUrl) {
        artists.push(data);
      }
    });

    return artists;
  } catch (error) {
    console.error('Error fetching featured artists:', error);
    return [];
  }
}

/**
 * Get Breakin' Bread label artists specifically
 */
export async function getBreakinBreadArtists(): Promise<ArtistProfile[]> {
  return (await getArtists({ labelId: 'breakin-bread' })).artists;
}

/**
 * Search artists by name
 */
export async function searchArtists(searchTerm: string): Promise<ArtistProfile[]> {
  try {
    // Fetch up to 500 artists ordered by name, filter client-side.
    // (Firestore has no native text search; fetch-all is fine for typical catalogue sizes.)
    const q = query(
      collection(db, ARTISTS_COLLECTION),
      orderBy('name'),
      limit(500)
    );
    const snapshot = await getDocs(q);

    const artists: ArtistProfile[] = [];
    const searchLower = searchTerm.toLowerCase().trim();

    snapshot.forEach((doc) => {
      const raw = doc.data();
      // Skip artists without a profile image (not ready for public display)
      if (!raw.imageUrl) return;
      // When search is empty, return all; otherwise filter by name
      if (!searchLower || (raw.name as string)?.toLowerCase().includes(searchLower)) {
        const genres = normalizeGenres(Array.isArray(raw.genres) && raw.genres.length > 0 ? raw.genres : (raw.genre ? [raw.genre as string] : []));
        artists.push({ id: doc.id, ...raw, genres } as ArtistProfile);
      }
    });

    return artists;
  } catch (error) {
    console.error('Error searching artists:', error);
    return [];
  }
}

/**
 * Create a new artist in the database
 */
export async function createArtist(artistData: Partial<ArtistProfile>): Promise<ArtistProfile | null> {
  try {
    const slug = (artistData.name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

    // Merge: spread artistData first so our explicit defaults always win
    // (prevents undefined values from artistData overwriting e.g. popularity: 0)
    // Exclude 'id' — that's a Firestore doc ID, not a field to store in the document body
    const { id: _id, ...artistDataWithoutId } = artistData as any;
    const merged = {
      ...artistDataWithoutId,
      name: artistData.name || '',
      slug,
      bio: artistData.bio || '',
      genres: artistData.genres || [],
      popularity: 0,          // always start at 0 so orderBy('popularity') includes this doc
      type: 'Artist',
      dateAdded: new Date().toISOString(),
      dataAiHint: `artist ${artistData.name}`,
    };

    // Strip undefined so Firestore doesn't reject the write
    const newArtist = stripUndefined(merged);

    const docRef = await addDoc(collection(db, ARTISTS_COLLECTION), newArtist);
    return { id: docRef.id, ...newArtist } as ArtistProfile;
  } catch (error) {
    console.error('Error creating artist:', error);
    return null;
  }
}

/**
 * Search for an artist by name, and if not found, create it
 */
export async function findOrCreateArtist(artistName: string): Promise<ArtistProfile | null> {
  try {
    const existingArtists = await searchArtists(artistName);

    if (existingArtists.length > 0) {
      const exactMatch = existingArtists.find(a => a.name.toLowerCase() === artistName.toLowerCase());
      if (exactMatch) return exactMatch;
      return existingArtists[0];
    }

    const newArtist = await createArtist({
      name: artistName,
      genres: [],
      bio: `Artist: ${artistName}`,
    });

    return newArtist;
  } catch (error) {
    console.error('Error in findOrCreateArtist:', error);
    return null;
  }
}

/**
 * Update an existing artist
 */
/** Recursively strip undefined values so Firestore doesn't reject the write */
function stripUndefined(obj: any): any {
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)])
    );
  }
  return obj;
}

export async function updateArtist(id: string, data: Partial<ArtistProfile>): Promise<boolean> {
  try {
    const docRef = doc(db, ARTISTS_COLLECTION, id);
    const { id: _, ...raw } = data;
    const updateData = stripUndefined(raw);
    await updateDoc(docRef, { ...updateData, updatedAt: new Date().toISOString() });
    // See updateWork in services/media.ts for why this event exists — lets
    // FavoritesBar refresh a stale header thumbnail without re-favoriting.
    if ('imageUrl' in updateData) {
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('bb:thumbnail-updated', { detail: { itemType: 'artist', itemId: id } }));
        }
      } catch { /* non-fatal */ }
    }
    return true;
  } catch (error) {
    console.error('Error updating artist:', error);
    return false;
  }
}

/**
 * Delete an artist
 */
export async function deleteArtist(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, ARTISTS_COLLECTION, id));
    // Cascade: remove all favorites that reference this artist
    try {
      const favSnap = await getDocs(
        query(collection(db, 'favorites'), where('itemType', '==', 'artist'), where('itemId', '==', id))
      );
      if (!favSnap.empty) {
        const batch = writeBatch(db);
        favSnap.docs.forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    } catch { /* Non-critical */ }
    return true;
  } catch (error) {
    console.error('Error deleting artist:', error);
    return false;
  }
}

/**
 * Toggle admin hide/show for an artist (super admin override).
 * Also propagates the adminHidden flag to any user favorites referencing this artist
 * so they disappear from / reappear in every user's FavoritesBar automatically.
 */
export async function toggleAdminHidden(id: string, hidden: boolean): Promise<boolean> {
  try {
    const docRef = doc(db, ARTISTS_COLLECTION, id);
    await updateDoc(docRef, { adminHidden: hidden, updatedAt: new Date().toISOString() });

    // Sync adminHidden to all favorites that reference this artist
    try {
      const favQ = query(
        collection(db, 'favorites'),
        where('itemType', '==', 'artist'),
        where('itemId', '==', id),
      );
      const favSnap = await getDocs(favQ);
      if (!favSnap.empty) {
        const batch = writeBatch(db);
        favSnap.docs.forEach(d => batch.update(d.ref, { adminHidden: hidden }));
        await batch.commit();
      }
    } catch {
      // Non-critical — favorites sync failure shouldn't block the main toggle
    }

    return true;
  } catch (error) {
    console.error('Error toggling admin hidden:', error);
    return false;
  }
}

/**
 * Get all artists (admin view - no filtering, includes hidden)
 */
export async function getAllArtistsAdmin(): Promise<ArtistProfile[]> {
  try {
    const q = query(collection(db, ARTISTS_COLLECTION), orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ArtistProfile));
  } catch (error) {
    console.error('Error fetching all artists (admin):', error);
    return [];
  }
}
